import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import {
    addRecentDocument,
    RecentDocumentItem,
    DocumentType
} from "../redux/reducers/RecentNavigation";

interface UseRecentDocumentOptions {
    docType: DocumentType;
    label: string;
    icon: string;
    l1?: string;
    l2?: string;
    enabled?: boolean; // Allow disabling tracking
}

/**
 * Hook to track when users view different document types
 * This helps users quickly return to their recent data
 */
export const useRecentDocument = (options: UseRecentDocumentOptions | null) => {
    const dispatch = useDispatch();
    const location = useLocation();

    useEffect(() => {
        if (!options || options.enabled === false) return;

        const { docType, label, icon, l1, l2 } = options;

        // Generate a unique ID based on document type and folder/project
        const id = `${docType}-${l1 || "all"}-${l2 || "all"}`;

        // Create the recent document item
        const item: RecentDocumentItem = {
            id,
            docType,
            label,
            url: `${location.pathname}${location.search}`,
            icon,
            timestamp: Date.now(),
            l1,
            l2,
        };

        // Delay adding to avoid tracking during rapid navigation
        const timeoutId = setTimeout(() => {
            dispatch(addRecentDocument(item));
        }, 1000); // 1 second delay to ensure user is actually viewing the page

        return () => clearTimeout(timeoutId);
    }, [
        options?.docType,
        options?.label,
        options?.l1,
        options?.l2,
        options?.enabled,
        location.pathname,
        location.search,
        dispatch
    ]);
};

